/**
 * QuickAccessPanel - Favorites, Recent, and Frequent Reports
 * Displayed in header dropdown for quick access to reports
 */

import React, { useState, useEffect } from 'react';
import { Star, Clock, TrendingUp, ChevronDown, X, Plus, Trash2 } from 'lucide-react';
import api from '@/services/api';

const QuickAccessPanel = ({ 
  userId = 'default', 
  theme = 'light',
  onReportSelect = null,
  isOpen = false,
  onClose = null
}) => {
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState('favorites');
  const [favorites, setFavorites] = useState([]);
  const [recent, setRecent] = useState([]);
  const [frequent, setFrequent] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadDashboardData();
    }
  }, [isOpen, userId]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/report-engine/dashboard/${userId}`);
      if (response.data.success) {
        setFavorites(response.data.data.favorites || []);
        setRecent(response.data.data.recent || []);
        setFrequent(response.data.data.frequent || []);
      }
    } catch (error) {
      console.error('Error loading quick access data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (reportCode, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/api/report-engine/favorites/${userId}/${reportCode}`);
      setFavorites(prev => prev.filter(f => f.code !== reportCode));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const handleReportClick = (report) => {
    onReportSelect?.(report);
    onClose?.();
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'favorites', label: 'Favorites', icon: Star, data: favorites, color: 'text-amber-500' },
    { id: 'recent', label: 'Recent', icon: Clock, data: recent, color: 'text-blue-500' },
    { id: 'frequent', label: 'Most Used', icon: TrendingUp, data: frequent, color: 'text-green-500' },
  ];

  const activeTabData = tabs.find(t => t.id === activeTab);

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      />
      
      {/* Panel */}
      <div 
        className={`absolute right-0 top-full mt-2 w-full rounded-xl border shadow-2xl z-50 overflow-hidden ${
          isDark ? 'bg-[#151515] border-[#333]' : 'bg-white border-gray-200'
        }`}
        data-testid="quick-access-panel"
      >
        {/* Header */}
        <div className={`px-4 py-3 border-b ${isDark ? 'border-[#333]' : 'border-gray-100'}`}>
          <div className="flex items-center justify-between">
            <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Quick Access
            </h3>
            <button 
              onClick={onClose}
              className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <X size={16} />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-3">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  activeTab === tab.id
                    ? isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'
                    : isDark ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <tab.icon size={12} className={activeTab === tab.id ? tab.color : ''} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : activeTabData.data.length === 0 ? (
            <div className="py-8 text-center">
              <activeTabData.icon className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
              <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                {activeTab === 'favorites' 
                  ? 'No favorites yet. Star reports to add them here.'
                  : activeTab === 'recent'
                  ? 'No recent reports.'
                  : 'No frequently used reports.'}
              </p>
            </div>
          ) : (
            <div className="py-2">
              {activeTabData.data.map((report, index) => (
                <button
                  key={`${report.code}-${index}`}
                  onClick={() => handleReportClick(report)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors ${
                    isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                  }`}
                  data-testid={`quick-access-${report.code}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'
                  }`}>
                    <activeTabData.icon size={14} />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {report.name}
                    </p>
                    <p className={`text-xs truncate ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                      {report.workspace}
                      {report.accessCount && ` • ${report.accessCount} views`}
                      {report.lastAccess && ` • ${new Date(report.lastAccess).toLocaleDateString()}`}
                    </p>
                  </div>
                  {activeTab === 'favorites' && (
                    <button
                      onClick={(e) => handleRemoveFavorite(report.code, e)}
                      className={`p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                        isDark ? 'hover:bg-red-500/20 text-red-400' : 'hover:bg-red-50 text-red-500'
                      }`}
                      title="Remove from favorites"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {activeTab === 'favorites' && favorites.length > 0 && (
          <div className={`px-4 py-2 border-t ${isDark ? 'border-[#333]' : 'border-gray-100'}`}>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
              Drag reports to reorder • Click to open
            </p>
          </div>
        )}
      </div>
    </>
  );
};

/**
 * QuickAccessButton - Trigger button for Quick Access Panel
 */
export const QuickAccessButton = ({ onClick, theme = 'light', hasItems = false }) => {
  const isDark = theme === 'dark';
  
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
      data-testid="quick-access-btn"
    >
      <span className="flex items-center gap-2">
        <Star size={16} className={hasItems ? 'text-amber-500 fill-amber-500' : ''} />
        <span>Quick Access</span>
      </span>
      <ChevronDown size={14} />
    </button>
  );
};

/**
 * FavoriteButton - Add/Remove from favorites button for reports
 */
export const FavoriteButton = ({ 
  reportCode, 
  userId = 'default', 
  theme = 'light',
  isFavorite = false,
  onToggle = null
}) => {
  const isDark = theme === 'dark';
  const [favorite, setFavorite] = useState(isFavorite);
  const [loading, setLoading] = useState(false);

  const handleToggle = async () => {
    setLoading(true);
    try {
      if (favorite) {
        await api.delete(`/api/report-engine/favorites/${userId}/${reportCode}`);
        setFavorite(false);
      } else {
        await api.post('/api/report-engine/favorites', { userId, reportCode });
        setFavorite(true);
      }
      onToggle?.(!favorite);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={loading}
      className={`p-2 rounded-lg transition-colors ${
        favorite
          ? 'text-amber-500'
          : isDark ? 'text-slate-500 hover:text-amber-400' : 'text-gray-400 hover:text-amber-500'
      }`}
      title={favorite ? 'Remove from favorites' : 'Add to favorites'}
      data-testid="favorite-btn"
    >
      <Star size={18} className={favorite ? 'fill-current' : ''} />
    </button>
  );
};

export default QuickAccessPanel;
